import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { JobDetail } from 'api';
import { FieldCard } from './FieldCard';
import { FieldGroupPanel } from './FieldGroupPanel';
import { EMPTY_DECISION, FieldDecisionState } from '../decisionState';
import { FieldGroup, groupFields, groupPrefix, instanceIndex } from '../fieldPath';

type FieldReviewPanelProps = {
  jobDetail: JobDetail;
  decisions: Record<string, FieldDecisionState>;
  onDecisionChange: (
    fieldName: string,
    patch: Partial<FieldDecisionState>
  ) => void;
  activeFieldName: string | null;
  onSelectField: (fieldName: string) => void;
  undecidedCount: number;
  onApproveAll: () => void;
  selectingFieldName: string | null;
  onToggleSelectReference: (fieldName: string) => void;
  /** Fields named in the last submit rejection. Their panels are expanded so the
   * reviewer can reach them. */
  errorFieldNames?: string[];
};

export const FieldReviewPanel = ({
  jobDetail,
  decisions,
  onDecisionChange,
  activeFieldName,
  onSelectField,
  undecidedCount,
  onApproveAll,
  selectingFieldName,
  onToggleSelectReference,
  errorFieldNames = [],
}: FieldReviewPanelProps) => {
  const { documentFields, groups } = useMemo(
    () => groupFields(jobDetail.fields),
    [jobDetail.fields]
  );

  // Panels start collapsed: a job can have 20 sites, and open panels would push
  // the document-level fields off-screen.
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  useEffect(() => {
    setExpandedKeys([]);
  }, [jobDetail.extractionJobKey]);

  // Additive, so panels the reviewer opened stay open.
  useEffect(() => {
    if (errorFieldNames.length === 0) return;
    const keys = errorFieldNames
      .map(name => {
        const prefix = groupPrefix(name);
        const index = instanceIndex(name);
        return prefix != null && index != null ? `${prefix}[${index}]` : null;
      })
      .filter((key): key is string => key != null);
    if (keys.length === 0) return;
    setExpandedKeys(prev => Array.from(new Set([...prev, ...keys])));
  }, [errorFieldNames]);

  const handleToggleExpanded = (key: string, expanded: boolean) =>
    setExpandedKeys(prev =>
      expanded
        ? Array.from(new Set([...prev, key]))
        : prev.filter(k => k !== key)
    );

  // Like Approve all, for one instance: marks undecided fields correct and
  // leaves decided ones alone.
  const handleMarkGroupCorrect = (group: FieldGroup) =>
    group.fields
      .filter(f => decisions[f.fieldName]?.isCorrect == null)
      .forEach(f => onDecisionChange(f.fieldName, { isCorrect: true }));

  if (jobDetail.fields.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        This job's extraction result has no fields. Submit is blocked —
        nothing to review.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle1">
          Fields ({jobDetail.fields.length})
        </Typography>
        <Button
          size="small"
          variant="outlined"
          disabled={undecidedCount === 0}
          onClick={onApproveAll}
        >
          Approve all
        </Button>
      </Box>

      {documentFields.map(field => (
        <FieldCard
          key={field.fieldName}
          field={field}
          decision={decisions[field.fieldName] ?? EMPTY_DECISION}
          active={activeFieldName === field.fieldName}
          onSelect={() => onSelectField(field.fieldName)}
          onDecisionChange={patch => onDecisionChange(field.fieldName, patch)}
          selectingReference={selectingFieldName === field.fieldName}
          onToggleSelectReference={() => onToggleSelectReference(field.fieldName)}
        />
      ))}

      {groups.length > 0 && (
        <Box sx={{ mt: documentFields.length > 0 ? 2 : 0 }}>
          {groups.map((group, i) => (
            <FieldGroupPanel
              key={group.key}
              group={group}
              // "Site 1, Site 2" by position, not the array index.
              ordinal={i + 1}
              decisions={decisions}
              onDecisionChange={onDecisionChange}
              activeFieldName={activeFieldName}
              onSelectField={onSelectField}
              selectingFieldName={selectingFieldName}
              onToggleSelectReference={onToggleSelectReference}
              onMarkGroupCorrect={handleMarkGroupCorrect}
              expanded={expandedKeys.includes(group.key)}
              onToggleExpanded={handleToggleExpanded}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
